import { supabase } from './supabase';
import { BUCKET, PHOTO_COLUMNS } from './gallery';

// Turning a chosen file into a stored gallery photo:
//  1. the file's first bytes must be a real JPEG, PNG or WebP (the browser's MIME type is not trusted);
//  2. it is decoded and redrawn as a JPEG of at most 2000 px on the long side. This removes all metadata, including
//     the GPS location phones store in photos of children, and keeps files small;
//  3. it is uploaded under a generated name in the album's folder (no overwriting), then its row is added;
//     if the row cannot be added the file is removed again.
// The bucket and database enforce the type, size, folder and name rules again on the server.
export const MAX_INPUT_BYTES = 20 * 1024 * 1024;
export const MAX_STORED_BYTES = 5 * 1024 * 1024;
export const MAX_DIMENSION = 2000;
export const MAX_FILES_PER_UPLOAD = 30;
const JPEG_QUALITY = 0.85;

export class UploadError extends Error {}

export const detectImageType = (bytes) => {
  const b = bytes ?? [];
  const at = (offset, values) => values.every((v, i) => b[offset + i] === v);
  if (at(0, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (at(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (at(0, [0x52, 0x49, 0x46, 0x46]) && at(8, [0x57, 0x45, 0x42, 0x50])) return 'image/webp';
  return null;
};

export const fitWithin = (width, height, max = MAX_DIMENSION) => {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

const readHeader = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, 16));
  });

export const prepareImage = async (file) => {
  if (file.size > MAX_INPUT_BYTES) throw new UploadError(`${file.name} is larger than 20 MB`);
  if (!detectImageType(await readHeader(file))) {
    throw new UploadError(`${file.name} is not a JPEG, PNG or WebP photo`);
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new UploadError(`${file.name} could not be read as a photo`);
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // transparent PNG areas become white rather than black
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) throw new UploadError(`${file.name} could not be converted`);
  if (blob.size > MAX_STORED_BYTES) throw new UploadError(`${file.name} is still larger than 5 MB after resizing`);
  return { blob, width, height };
};

// Uploads one photo into the album and returns its new row.
export const uploadPhoto = async (albumId, file, { altText, sortOrder }) => {
  const { blob, width, height } = await prepareImage(file);
  const path = `${albumId}/${crypto.randomUUID()}.jpg`;
  const storage = supabase.storage.from(BUCKET);
  const { error: uploadError } = await storage.upload(path, blob, {
    contentType: 'image/jpeg', cacheControl: '31536000', upsert: false
  });
  if (uploadError) {
    console.error('Gallery upload failed', uploadError);
    throw new UploadError(`${file.name} could not be uploaded`);
  }
  const { data, error } = await supabase
    .from('gallery_images')
    .insert({
      album_id: albumId, storage_path: path, alt_text: altText, size_bytes: blob.size, mime_type: 'image/jpeg',
      width, height, sort_order: sortOrder
    })
    .select(PHOTO_COLUMNS)
    .single();
  if (error) {
    console.error('Gallery photo row failed', error);
    await storage.remove([path]);
    throw new UploadError(`${file.name} could not be added to the album`);
  }
  return data;
};

// Removes files after their rows are gone. A file that cannot be removed is logged, never shown to visitors.
export const removeFiles = async (paths) => {
  if (!paths.length) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) console.error('Gallery files could not be removed', paths, error);
};
